import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, X } from "lucide-react";
import { z } from "zod";

const contactSchema = z.object({
  name: z.string().trim().min(1, "Nome richiesto").max(100, "Nome troppo lungo (max 100 caratteri)"),
  email: z.string().trim().email("Email non valida").max(255, "Email troppo lunga"),
  phone: z.string().trim().max(20, "Telefono troppo lungo").optional().or(z.literal("")),
  message: z.string().trim().min(10, "Messaggio troppo breve (min 10 caratteri)").max(2000, "Messaggio troppo lungo (max 2000 caratteri)")
});

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const ContactFormSection = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const remainingSlots = 3 - selectedImages.length;
    const newImages = files.slice(0, remainingSlots);
    
    if (files.length > remainingSlots) {
      toast({
        title: "Attenzione",
        description: `Puoi caricare massimo 3 immagini. Sono state selezionate le prime ${remainingSlots}.`,
        variant: "destructive"
      });
    }

    // Validate file sizes
    const invalidFiles = newImages.filter(file => file.size > MAX_FILE_SIZE);
    if (invalidFiles.length > 0) {
      toast({
        title: "File troppo grande",
        description: "Ogni immagine deve essere massimo 5MB",
        variant: "destructive"
      });
      return;
    }

    // Validate file types
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const invalidTypes = newImages.filter(file => !validTypes.includes(file.type));
    if (invalidTypes.length > 0) {
      toast({
        title: "Formato non valido",
        description: "Sono accettate solo immagini JPG, PNG o WebP",
        variant: "destructive"
      });
      return;
    }

    setSelectedImages([...selectedImages, ...newImages]);
    
    // Create preview URLs
    const newPreviews = newImages.map(file => URL.createObjectURL(file));
    setPreviewUrls([...previewUrls, ...newPreviews]);
  };

  const removeImage = (index: number) => {
    const newImages = selectedImages.filter((_, i) => i !== index);
    const newPreviews = previewUrls.filter((_, i) => i !== index);
    URL.revokeObjectURL(previewUrls[index]);
    setSelectedImages(newImages);
    setPreviewUrls(newPreviews);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const name = formData.get("name") as string;
    const email = formData.get("email") as string;
    const phone = formData.get("phone") as string;
    const message = formData.get("message") as string;

    // Validate inputs
    try {
      contactSchema.parse({ name, email, phone, message });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Errore di validazione",
          description: error.errors[0].message,
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }
    }

    try {
      // Upload images to storage
      const imageUrls: string[] = [];
      
      for (const image of selectedImages) {
        const fileExt = image.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = `${Date.now()}_${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('contact-images')
          .upload(filePath, image);

        if (uploadError) throw uploadError;

        // Store just the file path, not a public URL
        // Admin.tsx will generate signed URLs when displaying
        imageUrls.push(filePath);
      }

      // Insert message into database
      const { error: insertError } = await supabase
        .from('contact_messages')
        .insert({
          name,
          email,
          phone,
          message,
          images: imageUrls
        });

      if (insertError) throw insertError;

      toast({
        title: "Messaggio inviato!",
        description: "Ti risponderemo il prima possibile.",
      });

      (e.target as HTMLFormElement).reset();
      setSelectedImages([]);
      previewUrls.forEach(url => URL.revokeObjectURL(url));
      setPreviewUrls([]);
    } catch (error) {
      toast({
        title: "Errore",
        description: "Si è verificato un errore. Riprova più tardi.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-6 section-alt">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8 animate-fade-in-up">
            <h2 className="mb-4 text-primary uppercase text-2xl md:text-4xl text-center" style={{ fontFamily: 'Verdana, sans-serif' }}>
              Contattaci
            </h2>
            <p className="text-muted-foreground" style={{ fontFamily: 'Verdana, sans-serif' }}>
              Compila il form e ti risponderemo al più presto
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-card rounded-2xl p-8 shadow-[var(--shadow-large)] space-y-6 animate-fade-in-up border border-border"
            style={{ animationDelay: "0.1s" }}
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">Nome e Cognome *</Label>
                <Input
                  id="name"
                  name="name"
                  required
                  maxLength={100}
                  placeholder="Mario Rossi"
                  className="border-border focus:ring-primary"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  maxLength={255}
                  placeholder="mario@example.com"
                  className="border-border focus:ring-primary"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Telefono</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                maxLength={20}
                placeholder="+39 123 456 7890"
                className="border-border focus:ring-primary"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Messaggio *</Label>
              <Textarea
                id="message"
                name="message"
                required
                minLength={10}
                maxLength={2000}
                rows={6}
                placeholder="Scrivi qui il tuo messaggio (minimo 10 caratteri)..."
                className="border-border focus:ring-primary resize-none"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="images">Immagini (max 3)</Label>
              <div className="space-y-4">
                {selectedImages.length < 3 && (
                  <div className="flex items-center gap-4">
                    <Input
                      id="images"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('images')?.click()}
                      className="w-full"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Carica immagini ({selectedImages.length}/3)
                    </Button>
                  </div>
                )}
                
                {previewUrls.length > 0 && (
                  <div className="grid grid-cols-3 gap-4">
                    {previewUrls.map((url, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={url}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-cta py-6 text-lg font-semibold"
            >
              {isSubmitting ? "Invio in corso..." : "Invia messaggio"}
            </Button>

            <p className="text-sm text-center text-muted-foreground">
              Ti risponderemo entro 24 ore. Per urgenze chiamaci al{" "}
              <a href="tel:+393466257639" className="text-primary hover:text-primary-light font-medium">
                +39 346 625 7639
              </a>
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactFormSection;
